import InfiniteScrollTrigger from 'components/InfiniteScrollTrigger'
import Spinner from 'components/Spinner'
import { useInfiniteScrollWithTrigger } from 'hooks/useInfiniteScrollWithTrigger'
import { buildTranslate } from 'locales'
import React from 'react'

const t = buildTranslate('common')

type InfiniteScrollContainerProps = {
  paginationId: string
  paginationType: string
  fetchAction: (params: { page: number; cancelToken?: any }) => any
  renderItem?: (recordId: string | number) => React.ReactNode
  onError?: (error: any) => void
  enabled?: boolean
  className?: string
  emptyText?: string
  initialLoadingText?: string
  loadMoreText?: string
  noMoreText?: string
  showLoadingSpinner?: boolean
  threshold?: number
  rootMargin?: string
  children?: (props: {
    recordIds: Array<number | string>
    hasMore: boolean | undefined
    isLoading: boolean
    isInitialLoading: boolean
    loadMore: () => Promise<void>
    reset: () => void
    currentPage: number | undefined
    totalPages: number | undefined
    totalCount: number | undefined
    triggerRef: React.RefObject<HTMLDivElement>
  }) => React.ReactNode
}

const InfiniteScrollContainer = ({
  paginationId,
  paginationType,
  fetchAction,
  renderItem,
  onError,
  enabled = true,
  className = '',
  emptyText = t('infinite_scroll.empty_text'),
  initialLoadingText = t('infinite_scroll.initial_loading'),
  loadMoreText = t('infinite_scroll.load_more'),
  noMoreText = t('infinite_scroll.no_more'),
  showLoadingSpinner = true,
  threshold = 0.1,
  rootMargin = '100px',
  children,
}: InfiniteScrollContainerProps) => {
  const infiniteScrollProps = useInfiniteScrollWithTrigger({
    paginationId,
    paginationType,
    fetchAction,
    onError,
    enabled,
    threshold,
    rootMargin,
  })

  const { recordIds, hasMore, isLoading, isInitialLoading, triggerRef } =
    infiniteScrollProps

  if (children) {
    return <>{children({ ...infiniteScrollProps, triggerRef })}</>
  }

  if (isInitialLoading) {
    return (
      <div className={`text-center py-xl ${className}`}>
        {showLoadingSpinner && <Spinner size="medium" className="mb-s" />}
        <div className="text-grey-70">{initialLoadingText}</div>
      </div>
    )
  }

  if (recordIds.length === 0 && !isLoading) {
    return (
      <div className={`text-center py-xl text-grey-50 ${className}`}>
        {emptyText}
      </div>
    )
  }

  return (
    <div className={className}>
      {renderItem &&
        recordIds.map(recordId => (
          <React.Fragment key={recordId}>{renderItem(recordId)}</React.Fragment>
        ))}

      {(hasMore === true || isLoading) && (
        <InfiniteScrollTrigger
          triggerRef={triggerRef}
          isLoading={isLoading}
          hasMore={hasMore}
          loadMoreText={loadMoreText}
          noMoreText={noMoreText}
          showLoadingSpinner={showLoadingSpinner}
        />
      )}
    </div>
  )
}

export default InfiniteScrollContainer
